import axios from "axios";

const VAYLA_BASE = "https://avoinapi.vaylapilvi.fi/viitekehysmuunnin/muunna";

/** The return values the road geometry lookup needs (katunimi, geometry, length, …). */
const PALAUTUSARVOT = [1, 2, 3, 4, 5, 6]
  .map((n) => `palautusarvot=${n}`)
  .join("&");

/**
 * A miss is not an HTTP error: the service answers 200 with a single feature
 * whose only property is `virheet` and whose geometry has no coordinates.
 * Everything that talks to this API has to check for it.
 */
export const roadLookupError = (data: any): string | null => {
  const virheet = data?.features?.[0]?.properties?.virheet;
  return typeof virheet === "string" && virheet.trim().length > 0
    ? virheet.trim()
    : null;
};

export const getRoadInfo = async (streetName: string, municipality: string) => {
  const vaylaUrl =
    `${VAYLA_BASE}?kuntanimi=${encodeURIComponent(municipality)}` +
    `&katunimi=${encodeURIComponent(
      streetName
    )}&valihaku=true&${PALAUTUSARVOT}`;
  const road = await axios.get(vaylaUrl);
  return road;
};

export const getRoadInfoFromXY = async (x: number, y: number) => {
  const vaylaUrl = `${VAYLA_BASE}?x=${x}&y=${y}`;
  const road = await axios.get(vaylaUrl);
  return road;
};

export interface NearestRoad {
  name: string;
  municipality: string;
  /** Distance in metres from the queried point to the road it snapped to. */
  distanceM: number;
}

/**
 * Snap an ETRS-TM35FIN coordinate to the nearest road in the national road
 * register and return its canonical name. Returns null when the point is not
 * near any road — the service reports that as a `virheet` too.
 *
 * The canonical name matters: feeding it back into `getRoadInfo` is what makes
 * the exact-match name lookup succeed for a user who could not spell it.
 */
export const findNearestRoad = async (
  easting: number,
  northing: number
): Promise<NearestRoad | null> => {
  const response = await getRoadInfoFromXY(easting, northing);

  if (roadLookupError(response.data)) return null;

  const properties = response.data?.features?.[0]?.properties;
  if (!properties?.katunimi || !properties?.kuntanimi) return null;

  return {
    name: properties.katunimi,
    municipality: properties.kuntanimi,
    distanceM: Number(properties.valimatka ?? 0),
  };
};
